import {
  LORE_DOCS,
  LORE_DOC_COUNT,
  LORE_FRAGMENT_COUNT,
  LORE_FRAGMENT_TO_DOC,
} from './catalog';
import type { LoreDoc, LoreFragment, LoreRewardView, LoreSection } from './types';
import { LORE_SECTIONS } from './types';
import { fetchArchive, unlockArchiveFragment } from '../api';

const STORAGE_KEY = 'chroma.lore.v1';
const TIER_WEIGHT: Record<number, number> = { 1: 6, 2: 4, 3: 2.5, 4: 1 };

export interface LorePick {
  fragment: LoreFragment;
  doc: LoreDoc;
}

export interface LoreCollectResult {
  fragment: LoreFragment;
  doc: LoreDoc;
  duplicate: boolean;
  docCompleted: boolean;
  reward: LoreRewardView;
}

export interface DocProgress {
  owned: number;
  total: number;
  state: 'none' | 'partial' | 'complete';
}

export interface SectionProgress {
  docsRecovered: number;
  docsComplete: number;
  docsTotal: number;
  fragmentsOwned: number;
  fragmentsTotal: number;
  missingDocs: number;
  incompleteDocs: number;
}

export interface OverallProgress {
  docsRecovered: number;
  docsComplete: number;
  docsTotal: number;
  fragmentsOwned: number;
  fragmentsTotal: number;
  missingDocs: number;
}

interface StoredArchive {
  owned: Record<string, number>;
}

export class LoreRepository {
  private counts = new Map<string, number>();
  private tokenProvider: (() => Promise<string | null>) | null = null;
  private listeners = new Set<() => void>();
  private syncPromise: Promise<void> | null = null;

  constructor() {
    this.loadLocal();
  }

  setTokenProvider(provider: (() => Promise<string | null>) | null): void {
    this.tokenProvider = provider;
    if (provider) void this.syncRemote();
  }

  onChange(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  get ownedCount(): number {
    return this.counts.size;
  }

  isOwned(fragmentId: string): boolean {
    return this.counts.has(fragmentId);
  }

  getDocProgress(doc: LoreDoc): DocProgress {
    const total = doc.fragments.length;
    const owned = doc.fragments.filter((fragment) => this.counts.has(fragment.id)).length;
    return {
      owned,
      total,
      state: owned === 0 ? 'none' : owned === total ? 'complete' : 'partial',
    };
  }

  getSectionProgress(section: LoreSection): SectionProgress {
    const docs = LORE_DOCS.filter((doc) => doc.section === section);
    let docsRecovered = 0;
    let docsComplete = 0;
    let fragmentsOwned = 0;
    let fragmentsTotal = 0;
    for (const doc of docs) {
      const progress = this.getDocProgress(doc);
      if (progress.owned > 0) docsRecovered += 1;
      if (progress.state === 'complete') docsComplete += 1;
      fragmentsOwned += progress.owned;
      fragmentsTotal += progress.total;
    }
    return {
      docsRecovered,
      docsComplete,
      docsTotal: docs.length,
      fragmentsOwned,
      fragmentsTotal,
      missingDocs: docs.length - docsRecovered,
      incompleteDocs: docs.length - docsComplete,
    };
  }

  getOverallProgress(): OverallProgress {
    let docsRecovered = 0;
    let docsComplete = 0;
    for (const doc of LORE_DOCS) {
      const progress = this.getDocProgress(doc);
      if (progress.owned > 0) docsRecovered += 1;
      if (progress.state === 'complete') docsComplete += 1;
    }
    return {
      docsRecovered,
      docsComplete,
      docsTotal: LORE_DOC_COUNT,
      fragmentsOwned: this.counts.size,
      fragmentsTotal: LORE_FRAGMENT_COUNT,
      missingDocs: LORE_DOC_COUNT - docsRecovered,
    };
  }

  pickFragment(protocol: number, exclude: Set<string> = new Set()): LorePick | null {
    const candidates: { doc: LoreDoc; fragment: LoreFragment; weight: number }[] = [];
    for (const doc of LORE_DOCS) {
      if (doc.minProtocol > protocol) continue;
      for (const fragment of doc.fragments) {
        if (this.counts.has(fragment.id) || exclude.has(fragment.id)) continue;
        candidates.push({
          doc,
          fragment,
          weight: TIER_WEIGHT[doc.tier] ?? 1,
        });
      }
    }
    if (!candidates.length) return null;
    const total = candidates.reduce((sum, candidate) => sum + candidate.weight, 0);
    let roll = Math.random() * total;
    for (const candidate of candidates) {
      roll -= candidate.weight;
      if (roll <= 0) return { doc: candidate.doc, fragment: candidate.fragment };
    }
    const last = candidates[candidates.length - 1]!;
    return { doc: last.doc, fragment: last.fragment };
  }

  collect(fragmentId: string): LoreCollectResult | null {
    const doc = LORE_FRAGMENT_TO_DOC.get(fragmentId);
    if (!doc) return null;
    const fragment = doc.fragments.find((item) => item.id === fragmentId);
    if (!fragment) return null;
    const duplicate = this.counts.has(fragmentId);
    this.counts.set(fragmentId, (this.counts.get(fragmentId) ?? 0) + 1);
    this.persistLocal();
    this.emit();
    const docCompleted = this.getDocProgress(doc).state === 'complete';
    const reward: LoreRewardView = {
      fragmentId,
      docId: doc.id,
      code: doc.code,
      title: doc.title,
      section: doc.section,
      completedDoc: docCompleted,
    };
    const provider = this.tokenProvider;
    if (provider) {
      void provider().then((token) => {
        if (token) {
          void unlockArchiveFragment(token, fragmentId).catch(() => {});
        }
      });
    }
    return { fragment, doc, duplicate, docCompleted, reward };
  }

  async syncRemote(): Promise<void> {
    if (this.syncPromise) return this.syncPromise;
    const provider = this.tokenProvider;
    if (!provider) return;
    this.syncPromise = (async () => {
      try {
        const token = await provider();
        if (!token) return;
        const remote = await fetchArchive(token);
        let changed = false;
        for (const [id, duplicates] of Object.entries(remote)) {
          if (!LORE_FRAGMENT_TO_DOC.has(id)) continue;
          if (this.counts.has(id)) continue;
          this.counts.set(id, Math.max(1, duplicates));
          changed = true;
        }
        if (changed) {
          this.persistLocal();
          this.emit();
        }
      } catch {
        // Local archive remains usable while the API is unavailable.
      } finally {
        this.syncPromise = null;
      }
    })();
    return this.syncPromise;
  }

  getSections(): typeof LORE_SECTIONS {
    return LORE_SECTIONS;
  }

  private loadLocal(): void {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as Partial<StoredArchive>;
      if (!parsed.owned || typeof parsed.owned !== 'object') return;
      for (const [id, count] of Object.entries(parsed.owned)) {
        if (typeof count === 'number' && count > 0) this.counts.set(id, count);
      }
    } catch {
      // start with a blank local archive
    }
  }

  private persistLocal(): void {
    try {
      const owned: Record<string, number> = {};
      for (const [id, count] of this.counts.entries()) owned[id] = count;
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ owned }));
    } catch {
      // storage unavailable
    }
  }

  private emit(): void {
    for (const listener of this.listeners) listener();
  }
}
